import type { CanonicalDocument } from '@besbello/design-core';
import { sortDiagnostics, summarize, type Diagnostic, type ValidationResult } from '../types.js';
import { ruleIdOf, isRuleInForce, type ValidationContext } from '../rules.js';
import { validateZone } from './zone.js';
import { validateResolution } from './resolution.js';
import { validateInks } from './inks.js';
import { validateStrokeAndText } from './stroke.js';

export { validateZone, validateResolution, validateInks, validateStrokeAndText };
export { collectInks, countInksForPricing } from './inks.js';

/** Soporte de la prenda: la sublimacion no tine en algodon, y eso no es negociable. */
function validateSubstrate(_doc: CanonicalDocument, ctx: ValidationContext): Diagnostic[] {
  const out: Diagnostic[] = [];
  const ruleId = ruleIdOf(ctx.technique);

  const substrate = ctx.substrate.trim().toLowerCase();
  const allowed = ctx.technique.allowedSubstrates.map((s) => s.trim().toLowerCase());
  if (!allowed.includes(substrate)) {
    out.push({
      code: 'SUBSTRATE_NOT_SUPPORTED',
      severity: 'error',
      unit: 'none',
      ruleId,
      params: { substrate: ctx.substrate, allowed: ctx.technique.allowedSubstrates.join(', ') },
    });
  }

  // R-7: las puntadas NO se generan. El paquete de bordado es una peticion de
  // digitalizacion; se avisa desde el principio, no al llegar al taller.
  if (ctx.technique.requiresExternalDigitizing) {
    out.push({ code: 'EXTERNAL_DIGITIZING_REQUIRED', severity: 'info', unit: 'none', ruleId });
  }

  return out;
}

/**
 * VALIDACION COMPLETA de un documento contra una zona, una tecnica y un soporte.
 *
 * PURA: mismos argumentos, mismos diagnosticos, en el navegador y en el
 * servidor. No lee el reloj (`ctx.now` se pasa), no toca la red y no consulta la
 * base de datos.
 */
export function validateDesign(doc: CanonicalDocument, ctx: ValidationContext): ValidationResult {
  const diagnostics: Diagnostic[] = [];

  if (!isRuleInForce(ctx.technique, ctx.now)) {
    // Una regla fuera de vigencia no valida nada: obliga a recalcular con la
    // vigente en lugar de dar por bueno un limite caducado.
    throw new Error(`la regla ${ruleIdOf(ctx.technique)} no esta vigente en ${ctx.now}`);
  }

  diagnostics.push(...validateZone(doc, ctx));
  diagnostics.push(...validateResolution(doc, ctx));
  diagnostics.push(...validateInks(doc, ctx));
  diagnostics.push(...validateStrokeAndText(doc, ctx));
  diagnostics.push(...validateSubstrate(doc, ctx));

  return summarize(sortDiagnostics(diagnostics));
}

export { validateSubstrate };
